import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';

const Mindmaps = () => {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadList = async () => {
    try {
      const res = await axios.get('/api/agent/mindmaps');
      const rows = res.data || [];
      setItems(rows);
      if (rows.length > 0 && !selectedId) setSelectedId(rows[0].id);
    } catch (e) {
      if (e?.response?.status === 403) return;
      toast.error('Failed to load mindmaps');
    }
  };

  const loadMap = async (id) => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await axios.get(`/api/agent/mindmaps/${id}`);
      setMap(res.data);
    } catch {
      toast.error('Failed to open mindmap');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(); }, []);
  useEffect(() => {
    const qp = new URLSearchParams(location.search);
    const openId = qp.get('open');
    if (openId) setSelectedId(openId);
  }, [location.search]);
  useEffect(() => { if (selectedId) loadMap(selectedId); }, [selectedId]);

  const tree = useMemo(() => {
    if (!map?.nodes) return [];
    const byParent = {};
    const roots = [];
    map.nodes.forEach((n) => {
      const p = n.parent_id || '__root__';
      if (!byParent[p]) byParent[p] = [];
      byParent[p].push(n);
    });
    const walk = (node) => ({ ...node, children: (byParent[node.id] || []).map(walk) });
    (byParent['__root__'] || []).forEach((r) => roots.push(walk(r)));
    return roots;
  }, [map]);

  const renderNode = (node) => (
    <li key={node.id} className="mb-2">
      <div className="inline-block px-3 py-1.5 rounded-md bg-purple-50 border border-purple-200 text-sm text-purple-900">
        {node.label}
      </div>
      {node.children?.length > 0 && (
        <ul className="ml-6 mt-2 border-l border-gray-200 pl-4">
          {node.children.map(renderNode)}
        </ul>
      )}
    </li>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900">Mindmaps</h1>
      <p className="text-gray-600 mt-2">Open and explore mindmaps generated by Agent Mode.</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card lg:col-span-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Saved Mindmaps</h3>
          <div className="space-y-2 max-h-[560px] overflow-auto">
            {items.length === 0 ? (
              <p className="text-sm text-gray-500">No mindmaps yet. Generate one from Agent Mode.</p>
            ) : items.map((it) => (
              <button
                key={it.id}
                onClick={() => setSelectedId(it.id)}
                className={`w-full text-left p-3 rounded-lg border ${selectedId === it.id ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
              >
                <p className="font-medium text-gray-900 truncate">{it.title}</p>
                <p className="text-xs text-gray-500 mt-1">{new Date(it.created_at).toLocaleString()}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="card lg:col-span-2">
          {!map ? (
            <p className="text-gray-500">Select a mindmap to view.</p>
          ) : loading ? (
            <p className="text-gray-500">Loading mindmap...</p>
          ) : (
            <>
              <h3 className="text-xl font-semibold text-gray-900">{map.title}</h3>
              <p className="text-xs text-gray-500 mt-1 mb-4">Created {new Date(map.created_at).toLocaleString()}</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <ul>{tree.map(renderNode)}</ul>
              </div>
              {map.markdown_outline && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-900 mb-2">Outline</h4>
                  <pre className="text-xs whitespace-pre-wrap bg-white border border-gray-200 rounded p-3 text-gray-700">{map.markdown_outline}</pre>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Mindmaps;
